import React from 'react';
import { Box } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HeroKPI from '../../../common/display/HeroKPI';
import { CashflowKPIs } from '../hooks/useCalculationPageData';

interface KPISectionProps {
    kpis: CashflowKPIs;
}

/**
 * Formatiert DSCR als Dezimalzahl mit 2 Nachkommastellen
 */
const formatDscr = (value: number | null): string => {
    if (value === null) return '—';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Formatiert IRR als Prozent mit 1 Nachkommastelle
 */
const formatIrr = (value: number | null): string => {
    if (value === null) return '—';
    return `${value.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
};

/**
 * KPI Section
 * Prominente Darstellung der wichtigsten Kennzahlen mit Sparklines und Icons
 * Verwendet die wiederverwendbare HeroKPI-Komponente
 */
const KPISection: React.FC<KPISectionProps> = ({ kpis }) => {
    return (
        <Box sx={{ mb: 4 }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: 3,
                }}
            >
                {/* Eigenkapital IRR */}
                <HeroKPI
                    title="Eigenkapital IRR"
                    value={formatIrr(kpis.irr)}
                    icon={<TrendingUpIcon sx={{ fontSize: 24 }} />}
                    subtitle="Internal Rate of Return"
                    description="Jährliche Verzinsung des eingesetzten Eigenkapitals über die Projektlaufzeit"
                />

                {/* Min DSCR */}
                <HeroKPI
                    title="Min DSCR"
                    value={formatDscr(kpis.minDscr)}
                    icon={<AccountBalanceIcon sx={{ fontSize: 24 }} />}
                    subtitle="Minimale Debt Service Coverage Ratio"
                    description="Niedrigster Deckungsgrad des Schuldendienstes während der Kreditlaufzeit"
                />

                {/* Ø DSCR */}
                <HeroKPI
                    title="Ø DSCR"
                    value={formatDscr(kpis.avgDscr)}
                    icon={<AccountBalanceIcon sx={{ fontSize: 24 }} />}
                    subtitle="Durchschnittliche Debt Service Coverage Ratio"
                    description="Durchschnittlicher Deckungsgrad des Schuldendienstes während der Kreditlaufzeit"
                />
            </Box>
        </Box>
    );
};

export default React.memo(KPISection);

